import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { HandleSubscriptionsComponent } from 'src/app/appDirectives/handle-subscriptions.directive';
import { IStore } from 'src/app/appModels/IStore';
import { PaymentGatewayResolver } from 'src/app/appResolvers/payment-gateway.resolver';
import { RestaurantInfoResolver } from 'src/app/appResolvers/restaurant-info.resolver';
import { SettingsResolver } from 'src/app/appResolvers/settings.resolver';
import { StoreResolver } from 'src/app/appResolvers/store.resolver';
import { CashMgmtService } from 'src/app/appServices/cash-mgmt.service';
import { ProcessingFeeService } from 'src/app/appServices/processing-fee.service';
import { SettingsService } from 'src/app/appServices/settings.service';
import { NumberUtility } from 'src/app/appUtilities/number.utility';
import {
  LOCAL_CARD_PAYMENT_CODE,
  PaymentMethods,
} from 'src/app/core/enums/enums';
import { ToastMessageService } from 'src/app/core/utilities/toast-message.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-charge-action-sheet',
  templateUrl: './charge-action-sheet.page.html',
  styleUrls: ['./charge-action-sheet.page.scss'],
  standalone: false,
})
export class ChargeActionSheetPage
  extends HandleSubscriptionsComponent
  implements OnInit
{
  @Input() dataPayload: any;
  @Input() calculateSplittedFee: any;

  basicInfo: any;
  paymentMethods: any[];
  stripeProcessingFees: any = {};
  selectedStore: IStore;

  platformFee = 0;
  spendablePoints: number;
  remainingAmount: number;
  loyaltyPointValue: number;
  serviceFeesReady = false;
  notEnoughPoints: boolean = false;
  isExpired: boolean = false;
  isLoyaltyMgmtEnabled: boolean = false;
  calculationsComplete: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private _paymentGatewayResolver: PaymentGatewayResolver,
    private infoStorage: RestaurantInfoResolver,
    private _settingResolver: SettingsResolver,
    private processingFeeService: ProcessingFeeService,
    private numberUtility: NumberUtility,
    private cashMgmtService: CashMgmtService,
    private settingsService: SettingsService,
    private toaster: ToastMessageService,
    private _storeResolver: StoreResolver,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {
    super();
  }

  ngOnInit() {
    this.getBasicInfo();
    this.subscribePaymentMethods();
    this.getloyaltyPointsValue();
    this.subscribeSelectedStore();

    console.log('Payload: ', this.dataPayload);

    this.dataPayload.originalTotalAmount = this.dataPayload.totalAmount;
    this.calculationsComplete = false;

    if (this.dataPayload.processingFee) {
      this.dataPayload.totalAmount -= this.dataPayload.processingFee;
      delete this.dataPayload.processingFee;
    }

    this.loadStripeProcessingFees();
  }

  calculateAllTotals() {
    this.calculationsComplete = false;

    // Reset totalAmount to original base amount
    this.dataPayload.totalAmount = this.dataPayload.originalTotalAmount;

    // If the selected payment method is not cash-like, apply additional calculations
    if (
      this.dataPayload.paymentMethod &&
      !this.isCashLikePaymentMethod(this.dataPayload.paymentMethod)
    ) {
      // this.dataPayload.totalAmount += this.calculateProcessingFee(
      //   this.dataPayload.paymentMethod
      // );
      this.dataPayload.platformFee = this.getPlatformFeeFromRules(
        this.dataPayload.totalAmount,
        this.stripeProcessingFees.platformFeeRules,
        this.stripeProcessingFees?.platformFee,
      );
      console.log(
        '[DEBUG] Platform Fee During Total Calculation:',
        this.dataPayload.platformFee,
      );
      // this.dataPayload.totalAmount += this.dataPayload.platformFee;
    }
    if (this.dataPayload.paymentMethod) {
      this.dataPayload.platformFee = 0;
    }

    this.calculateSpendablePoints();
    this.calculationsComplete = true;
    this.cdr.detectChanges();

    console.log('[DEBUG] Final Total Amount:', this.dataPayload.totalAmount);
  }

  subscribeSelectedStore() {
    this.handleSubscription(this._storeResolver.selectedStore, (res) => {
      if (res) {
        this.getLoyaltyMgmtConfig(res.id);
      }
    });
  }

  getloyaltyPointsValue() {
    this.handleSubscription(
      this.cashMgmtService.getloyaltyPoints(),
      (res) => {
        this.loyaltyPointValue = Number(res?.value) || 0;
        this.calculateSpendablePoints();
      },
      (err) => {
        this.toaster.errorToastWithClose('Error: ' + err);
      },
    );
  }

  isSpecialTenantAndCoupon(): boolean {
    return (
      this.basicInfo?.restaurantName === 'Bluntley' &&
      this.dataPayload?.couponCode?.toUpperCase() === 'OWNER'
    );
  }

  calculateSpendablePoints() {
    let totalAmount = this.dataPayload?.totalAmount || 0;

    if (this.isSpecialTenantAndCoupon()) {
      totalAmount = this.dataPayload?.originalTotalAmount || totalAmount;
    }

    const loyaltyPoints = this.dataPayload?.customer?.loyaltyPoints || 0;
    if (!this.loyaltyPointValue || this.loyaltyPointValue <= 0) {
      this.spendablePoints = 0;
      this.remainingAmount = totalAmount;
      this.notEnoughPoints = false;
      return;
    }

    const pointsForTotalAmount = Math.ceil(
      totalAmount * this.loyaltyPointValue,
    );
    this.spendablePoints = Math.min(pointsForTotalAmount, loyaltyPoints);

    const remainingAmountRaw =
      totalAmount - this.spendablePoints / this.loyaltyPointValue;
    this.remainingAmount = Number(remainingAmountRaw) || 0;
    this.notEnoughPoints = loyaltyPoints < pointsForTotalAmount;

    const orderDate = new Date(this.dataPayload?.dateTime).getTime();
    if (this.dataPayload?.customer?.loyaltyPointsExpiration < orderDate) {
      this.isExpired = true;
    }
  }

  updateLoyaltyPointsBalance() {
    const loyaltyPoints = this.dataPayload?.customer?.loyaltyPoints || 0;
    this.dataPayload.customer.loyaltyPoints =
      loyaltyPoints - this.spendablePoints;
    this.dataPayload.loyaltyPointsSpent = this.spendablePoints;
  }

  onSelectPaymentGateway(method) {
    if (
      !this.serviceFeesReady &&
      !this.isFeeFreeLocalPaymentMethod(method) &&
      method.title !== 'Loyalty Points'
    ) {
      this.toaster.warningToastWithClose(
        this.translateService.instant('Please wait, payment fees are still loading.')
      );
      return;
    }

    if (method.title === 'Loyalty Points' && this.notEnoughPoints) {
      this.toaster.errorToastWithClose(
        this.translateService.instant('NotEnoughLoyaltyPoints'),
      );
      return;
    }

    this.dataPayload.paymentMethod = method;
    this.dataPayload.processingFee =
      this.calculateProcessingFee(this.dataPayload.paymentMethod) || 0;
    this.dataPayload.platformFee =
      this.isFeeFreeLocalPaymentMethod(method) ||
      method.title === 'Loyalty Points'
        ? 0
        : this.getPlatformFeeFromRules(
            this.dataPayload.totalAmount,
            this.stripeProcessingFees.platformFeeRules,
            this.stripeProcessingFees?.platformFee,
          );

    // if (method.title !== PaymentMethods.CashOnDelivery) {
    //   this.dataPayload.totalAmount =
    //     (this.dataPayload.totalAmount || 0) + this.dataPayload.processingFee;
    //   console.log(
    //     '[DEBUG] Total Amount After Adding Platform Fee:',
    //     this.dataPayload.totalAmount
    //   );
    // }

    if (method.title === 'Loyalty Points') {
      this.updateLoyaltyPointsBalance();
    }

    this.dismissModal(true);
  }

  subscribePaymentMethods() {
    this.handleSubscription(
      this._paymentGatewayResolver.paymentMethods,
      (res) => {
        if (res) {
          this.paymentMethods = this.buildPaymentMethods(res);
        }
      },
    );
  }

  subscribeStripeProcessingFees() {
    this.handleSubscription(
      this._settingResolver.stripeProcessingFee,
      (res) => {
        this.stripeProcessingFees = res || {};
        this.serviceFeesReady = true;
        this.calculateAllTotals();
      },
    );
  }

  loadStripeProcessingFees() {
    this.serviceFeesReady = false;
    this.handleSubscription(
      this.settingsService.getStripeProcessingFee(),
      (res) => {
        this.stripeProcessingFees = res || {};
        this.serviceFeesReady = true;
        this.calculateAllTotals();
      },
      () => {
        this.subscribeStripeProcessingFees();
        this._settingResolver.getStripeProcessingFee();
      },
    );
  }

  calculateProcessingFee(paymentMethod?: any): number {
    if (!paymentMethod || !paymentMethod.name) {
      return 0;
    }

    if (
      paymentMethod.title === PaymentMethods.CardReader ||
      this.isFeeFreeLocalPaymentMethod(paymentMethod)
    ) {
      return 0;
    }

    if (this.calculateSplittedFee) {
      const splittedFee = this.calculateSplittedFee.find(
        (fee) => fee.title === paymentMethod.title,
      );
      return splittedFee ? splittedFee.fee : 0;
    }

    if (this.isFeeFreeLocalPaymentMethod(paymentMethod)) {
      return 0;
    } else {
      return this.processingFeeService.calculateStripeProcessingFee(
        paymentMethod.title,
        this.dataPayload.totalAmount,
        this.stripeProcessingFees,
      );
    }
  }

  dismissModal(result: boolean) {
    if (result === false) {
      // If canceled, restore the original amount
      this.dataPayload.totalAmount = this.dataPayload.originalTotalAmount;
      delete this.dataPayload.paymentMethod; // Clear the payment method
    }
    this.modalCtrl.dismiss();
    this.cdr.detectChanges(); // Ensure UI reflects the reset amount
  }

  getBasicInfo() {
    // localStorage.removeItem('basic-info');
    this.handleSubscription(this.infoStorage.restaurantBasicInfo, (res) => {
      if (res) {
        this.basicInfo = res;
      }
    });
  }

  getPlatformFeeFromRules(
    totalAmount: number,
    platformFeeRules: any[],
    defaultPlatformFee: number,
  ): number {
    return this.processingFeeService.getPlatformFeeFromRules(
      totalAmount,
      platformFeeRules,
      defaultPlatformFee,
    );
  }

  getLoyaltyMgmtConfig(storeId: number) {
    this.handleSubscription(
      this.cashMgmtService.getConfigurationByStore('loyaltyMgmt', storeId),
      (res) => {
        this.isLoyaltyMgmtEnabled = res?.value === '1';
      },
      () => {
        this.isLoyaltyMgmtEnabled = false;
      }
    );
  }

  private buildPaymentMethods(methods: any[]): any[] {
    const paymentMethods = (methods || []).map((method) =>
      this.isLocalCardPaymentMethod(method)
        ? {
            ...method,
            name: PaymentMethods.LocalCard,
            title: PaymentMethods.LocalCard,
          }
        : method
    );

    if (paymentMethods.some((method) => this.isLocalCardPaymentMethod(method))) {
      return paymentMethods;
    }

    const localCardPaymentMethod = {
      id: -2,
      name: PaymentMethods.LocalCard,
      active: true,
      title: PaymentMethods.LocalCard,
      description:
        'Card or QR payment completed outside Tapgo payment gateways, reported as card.',
      type: 'Card',
      code: LOCAL_CARD_PAYMENT_CODE,
    };

    const firstNonCardIndex = paymentMethods.findIndex((method) =>
      this.isCashLikePaymentMethod(method) || method?.title === 'Loyalty Points',
    );

    if (firstNonCardIndex === -1) {
      return [...paymentMethods, localCardPaymentMethod];
    }

    return [
      ...paymentMethods.slice(0, firstNonCardIndex),
      localCardPaymentMethod,
      ...paymentMethods.slice(firstNonCardIndex),
    ];
  }

  private isLocalCardPaymentMethod(method?: any): boolean {
    return method?.code === LOCAL_CARD_PAYMENT_CODE;
  }

  private isCashLikePaymentMethod(method?: any): boolean {
    return (
      method?.title === PaymentMethods.CashOnDelivery ||
      this.isLocalCardPaymentMethod(method)
    );
  }

  private isFeeFreeLocalPaymentMethod(method?: any): boolean {
    return this.isCashLikePaymentMethod(method);
  }
}
